import * as RSUSoftwareListConstants from './RSUSoftwareListConstants';
import moment from 'moment';

/**
 * Initial state for RSU Software List management
 */
const initialState = {
    // Core data
    softwareList: [],
    filteredSoftwareList: [],
    productLineOptions: [],
    stopShipmentOptions: [],
    productLineWithInstrumentsOptions: [],
    pollingInterval: 5000,
    
    // Loading and error states
    isFetching: false,
    error: null,
    
    // Selection state
    selectedSoftwareIds: [],

    // Filter parameters
    filterParams: {
        productLineFilter: [],
        instrumentModelFilter: [],
        instrumentModFilter: '',
        itemRefNumberFilter: '',
        softwareNameFilter: '',
        softwareVersionFilter: '',
        userNameFilter: '',
        startDateFilter: null,
        endDateFilter: null,
        stopShipmentFilter: 'All',
    },

    // Modal states
    showDeleteConfirmation: false,
    showStopShipConfirmation: false,
    isStopShipAction: false,
    
    // Stop shipment operation state
    individualStopShipDetails: {
        itemRefNumber: null,
        currentStatus: null,
        newStatus: null,
        softwareName: null,
        softwareVersion: null,
        stopshipmentDateTime: null
    },
    isStopShipmentInProgress: false,
    
    // Validation and message states
    dateValidationError: {
        show: false,
        message: ""
    },
    messageModal: {
        show: false,
        message: "",
        isError: false
    },
};

/**
 * Applies all active filters to the software list
 */
const applyAllFilters = (list, filters) => {
    if (!Array.isArray(list) || list.length === 0) {
        return [];
    }

    let filtered = [...list];

    // Apply multiselect filters
    filtered = applyMultiselectFilters(filtered, filters);
    
    // Apply text filters
    filtered = applyTextFilters(filtered, filters);
    
    // Apply date filters
    filtered = applyDateFilters(filtered, filters);
    
    // Apply stop shipment filter
    filtered = applyStopShipmentFilter(filtered, filters);

    return filtered;
};

/**
 * Applies multiselect filters (product line and instrument model)
 */
const applyMultiselectFilters = (list, filters) => {
    let filtered = [...list];

    if (filters.productLineFilter?.length > 0) {
        const selectedValues = filters.productLineFilter.map(filter => filter.value);
        filtered = filtered.filter(item => selectedValues.includes(item.productLine));
    }

    if (filters.instrumentModelFilter?.length > 0) {
        const selectedValues = filters.instrumentModelFilter.map(filter => filter.value);
        filtered = filtered.filter(item => selectedValues.includes(item.instrumentModel));
    }

    return filtered;
};

/**
 * Applies text-based filters
 */
const applyTextFilters = (list, filters) => {
    let filtered = [...list];

    const textFilters = [
        { key: 'instrumentModFilter', field: 'instrumentMod' },
        { key: 'itemRefNumberFilter', field: 'itemRefNumber' },
        { key: 'softwareNameFilter', field: 'softwareName' },
        { key: 'softwareVersionFilter', field: 'softwareVersion' },
        { key: 'userNameFilter', field: 'userName' }
    ];

    textFilters.forEach(({ key, field }) => {
        if (filters[key]?.trim()) {
            const filterValue = filters[key].toLowerCase();
            filtered = filtered.filter(item =>
                item[field]?.toLowerCase().includes(filterValue)
            );
        }
    });

    return filtered;
};

/**
 * Applies date range filters
 */
const applyDateFilters = (list, filters) => {
    const startDate = filters.startDateFilter
        ? moment(filters.startDateFilter, "YYYY-MM-DD").startOf('day')
        : null;

    const endDate = filters.endDateFilter
        ? moment(filters.endDateFilter, "YYYY-MM-DD").endOf('day')
        : moment().endOf('day');

    if (!startDate && !endDate) {
        return list;
    }

    return list.filter(item => {
        if (!item.dateTimeUploaded) {
            return false;
        }

        const itemDate = moment(item.dateTimeUploaded, "DD/MM/YYYY HH:mm:ss");
        if (!itemDate.isValid()) {
            return false;
        }

        const isAfterStart = !startDate || itemDate.isSameOrAfter(startDate);
        const isBeforeEnd = !endDate || itemDate.isSameOrBefore(endDate);

        return isAfterStart && isBeforeEnd;
    });
};

/**
 * Applies stop shipment status filter
 */
const applyStopShipmentFilter = (list, filters) => {
    if (!filters.stopShipmentFilter || filters.stopShipmentFilter === "All") {
        return list;
    }

    const statusMap = {
        "Stopshipment Applied": "APPLIED",
        "Stopshipment Released": "RELEASED"
    };

    const targetStatus = statusMap[filters.stopShipmentFilter];
    return targetStatus ? list.filter(item => item.stopShipment === targetStatus) : list;
};


/**
 * RSU Software List Reducer
 * Handles all state management for the RSU software list operations
 */
const RSUSoftwareListReducer = (state = initialState, action) => {
    switch (action.type) {
        case RSUSoftwareListConstants.SET_SOFTWARE_FILTER_PARAM:
            return {
                ...state,
                filterParams: {
                    ...state.filterParams,
                    [action.payload.paramName]: action.payload.value
                },
                selectedSoftwareIds: []
            };

        case RSUSoftwareListConstants.APPLY_SOFTWARE_FILTERS_REQUEST:
            return {
                ...state,
                isFetching: true,
                error: null,
            };
            
        case RSUSoftwareListConstants.APPLY_SOFTWARE_FILTERS_SUCCESS:
            return {
                ...state,
                isFetching: false,
                filteredSoftwareList: applyAllFilters(state.softwareList, state.filterParams),
                selectedSoftwareIds: []
            };
            
        case RSUSoftwareListConstants.CLEAR_SOFTWARE_FILTERS:
            const clearedFilterParams = {
                productLineFilter: [],
                instrumentModelFilter: [],
                instrumentModFilter: '',
                itemRefNumberFilter: '',
                softwareNameFilter: '',
                softwareVersionFilter: '',
                userNameFilter: '',
                startDateFilter: null,
                endDateFilter: null,
                stopShipmentFilter: 'All',
            };
            return {
                ...state,
                filterParams: clearedFilterParams,
                filteredSoftwareList: applyAllFilters(state.softwareList, clearedFilterParams),
                selectedSoftwareIds: [],
            };

        case RSUSoftwareListConstants.GET_SOFTWARE_LIST_REQUEST:
            return {
                ...state,
                isFetching: true,
                error: null,
            };
            
        case RSUSoftwareListConstants.GET_SOFTWARE_LIST_SUCCESS:
            const apiResult = action.payload.result || action.payload;
            const softwareList = Array.isArray(apiResult) ? apiResult : (apiResult.softwareList || []);
            const productLineOptions = apiResult.productLineOptions || [];
            const stopShipmentOptions = apiResult.stopShipmentOptions || [];
            const productLineWithInstrumentsOptions = apiResult.productLineWithInstrumentsOptions || [];
            const pollingInterval =  apiResult.pollingInterval || 5000;

            return {
                ...state,
                softwareList,
                filteredSoftwareList: applyAllFilters(softwareList, state.filterParams),
                productLineOptions,
                stopShipmentOptions,
                productLineWithInstrumentsOptions,
                isFetching: false,
                pollingInterval,
            };
            
        case RSUSoftwareListConstants.GET_SOFTWARE_LIST_FAILURE:
            return {
                ...state,
                isFetching: false,
                error: action.payload.error,
            };

        case RSUSoftwareListConstants.SET_SELECTED_SOFTWARE_IDS:
            return {
                ...state,
                selectedSoftwareIds: action.payload.ids,
            };

        // Delete confirmation modal actions
        case RSUSoftwareListConstants.SHOW_DELETE_CONFIRMATION:
            return { ...state, showDeleteConfirmation: true };
            
        case RSUSoftwareListConstants.HIDE_DELETE_CONFIRMATION:
            return { ...state, showDeleteConfirmation: false };

        // Delete software actions
        case RSUSoftwareListConstants.DELETE_SOFTWARE_REQUEST:
            return { 
                ...state, 
                isFetching: true, 
                error: null 
            };
            
        case RSUSoftwareListConstants.DELETE_SOFTWARE_SUCCESS:
            // Extract item reference numbers from the deleted items
            const deletedItemRefNumbers = action.payload.deletedItems.map(item => item.itemrefnumber);
            
            const remainingSoftwareList = state.softwareList.filter(
                item => !deletedItemRefNumbers.includes(item.itemRefNumber)
            );
            
            return {
                ...state,
                softwareList: remainingSoftwareList,
                filteredSoftwareList: applyAllFilters(remainingSoftwareList, state.filterParams),
                selectedSoftwareIds: [],
                isFetching: false,
                showDeleteConfirmation: false,
                messageModal: {
                    show: true,
                    message: action.payload.message || 
                        `Successfully deleted ${deletedItemRefNumbers.length} software package(s): ${deletedItemRefNumbers.join(", ")}`,
                    isError: false
                }
            };
            
        case RSUSoftwareListConstants.DELETE_SOFTWARE_FAILURE:
            return {
                ...state,
                isFetching: false,
                error: action.payload.error,
                showDeleteConfirmation: false,
                messageModal: {
                    show: true,
                    message: action.payload.error || "Failed to delete software package(s). Please try again.",
                    isError: true
                }
            };

        // Stop shipment confirmation modal actions
        case RSUSoftwareListConstants.SHOW_STOP_SHIP_CONFIRMATION:
            return { ...state, showStopShipConfirmation: true };
            
        case RSUSoftwareListConstants.HIDE_STOP_SHIP_CONFIRMATION:
            return {
                ...state,
                showStopShipConfirmation: false,
                isStopShipAction: false
            };

        // Update stop shipment actions
        case RSUSoftwareListConstants.UPDATE_SOFTWARE_STOP_SHIPMENT_REQUEST:
            return {
                ...state,
                isFetching: true,
                isStopShipmentInProgress: true,
                error: null
            };
            
        case RSUSoftwareListConstants.UPDATE_SOFTWARE_STOP_SHIPMENT_SUCCESS:
            const { itemRefNumber, stopShipmentStatus, changedBy } = action.payload;
            
            // Extract software details BEFORE resetting individualStopShipDetails
            const softwareName = (state.individualStopShipDetails?.softwareName || '').trim() || itemRefNumber;
            const softwareVersion = (state.individualStopShipDetails?.softwareVersion || '').trim();
            const actionText = stopShipmentStatus === 'APPLIED' ? 'applied' : 'released';
            const versionText = softwareVersion ? ` (v${softwareVersion})` : '';
            const successMessage = `Stop shipment has been successfully ${actionText} for ${softwareName}${versionText}`;
            const stopshipmentDate = state.individualStopShipDetails?.stopshipmentDateTime || moment().utc().format("DD/MM/YYYY HH:mm:ss");
            const updatedList = state.softwareList.map(item => {
                
                if (item.itemRefNumber === itemRefNumber) {
                    return {
                        ...item,
                        stopShipment: stopShipmentStatus,
                        stopShipmentBy: changedBy,
                        stopshipmentDateTime: stopshipmentDate,
                    };
                }
                return item;
            });
            
            return {
                ...state,
                softwareList: updatedList,
                filteredSoftwareList: applyAllFilters(updatedList, state.filterParams),
                isFetching: false,
                isStopShipmentInProgress: false,
                error: null,
                showStopShipConfirmation: false,
                messageModal: {
                    show: true,
                    message: successMessage,
                    isError: false
                }
            };
            
        case RSUSoftwareListConstants.UPDATE_SOFTWARE_STOP_SHIPMENT_FAILURE:
            return {
                ...state,
                isFetching: false,
                isStopShipmentInProgress: false,
                error: action.payload.error,
                showStopShipConfirmation: false,
                individualStopShipDetails: {
                    itemRefNumber: null,
                    currentStatus: null,
                    newStatus: null,
                    softwareName: null,
                    softwareVersion: null,
                    stopshipmentDateTime:null
                },
                messageModal: {
                    show: true,
                    message: action.payload.error || "Failed to update stop shipment status. Please try again.",
                    isError: true
                }
            };

        // Individual stop shipment details
        case RSUSoftwareListConstants.SET_INDIVIDUAL_STOP_SHIP_DETAILS:
            return {
                ...state,
                individualStopShipDetails: {
                    itemRefNumber: action.payload.itemRefNumber,
                    currentStatus: action.payload.currentStatus,
                    newStatus: action.payload.newStatus,
                    softwareName: action.payload.softwareName,
                    softwareVersion: action.payload.softwareVersion,
                },
                isStopShipAction: action.payload.newStatus === 'APPLIED',
                showStopShipConfirmation: true
            };

        // Date validation error actions
        case RSUSoftwareListConstants.SHOW_DATE_VALIDATION_ERROR:
            return {
                ...state,
                dateValidationError: {
                    show: true,
                    message: action.payload.message
                }
            };
            
        case RSUSoftwareListConstants.HIDE_DATE_VALIDATION_ERROR:
            return {
                ...state,
                dateValidationError: {
                    show: false,
                    message: ""
                }
            };

        // Message modal actions
        case RSUSoftwareListConstants.SHOW_MESSAGE_MODAL:
            return {
                ...state,
                messageModal: {
                    show: true,
                    message: action.payload.message,
                    isError: action.payload.isError || false
                }
            };
            
        case RSUSoftwareListConstants.HIDE_MESSAGE_MODAL:
            return {
                ...state,
                messageModal: {
                    show: false,
                    message: "",
                    isError: false
                }
            };

        case RSUSoftwareListConstants.POLL_UPLOAD_STATUS_REQUEST:
            return {
                ...state,
                isPollingStatus: true
            };

        case RSUSoftwareListConstants.POLL_UPLOAD_STATUS_SUCCESS:
            // Update only the upload status for specific items without re-rendering the entire list
            const statusUpdates = action.payload; // Array of { itemRefNumber, uploadStatus }
            const updatedSoftwareList = state.softwareList.map(item => {
                const statusUpdate = statusUpdates.find(update => update.itemRefNumber === item.itemRefNumber);
                if (statusUpdate) {
                    return {
                        ...item,
                        uploadStatus: statusUpdate.uploadStatus
                    };
                }
                return item;
            });

            const updatedFilteredList = state.filteredSoftwareList.map(item => {
                const statusUpdate = statusUpdates.find(update => update.itemRefNumber === item.itemRefNumber);
                if (statusUpdate) {
                    return {
                        ...item,
                        uploadStatus: statusUpdate.uploadStatus
                    };
                }
                return item;
            });

            return {
                ...state,
                softwareList: updatedSoftwareList,
                filteredSoftwareList: updatedFilteredList,
                isPollingStatus: false
            };

        case RSUSoftwareListConstants.POLL_UPLOAD_STATUS_FAILURE:
            return {
                ...state,
                isPollingStatus: false
            };

        default:
            return state;
    }
};

export default RSUSoftwareListReducer;
